import {
  Button,
  Checkbox,
  FormControlLabel,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  SvgIcon,
  Typography,
} from '@material-ui/core';
import classNames from 'classnames';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ValidateIcon } from 'components/Icons';
import DownloadIcon from 'components/Icons/DownloadIcon';
import MemoPdfIcon from 'components/Icons/PdfIcon';
import Loading from 'components/Loading';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import { ReportType } from 'enums/reports';
import {
  useActiveUnbalancedContractsToDate,
  useClientsWithActiveContractAndContractsClosedThisYear,
  useClosureById,
  useContractsClosedThisYear,
} from 'hooks';
import MemoInfoIcon from 'components/Icons/InfoIcon';
import NotFound from 'pages/NotFound/NotFound';
import { colors } from 'theme';
import { Client } from 'types';
import { calculatePercentageWithPrecision } from 'services/math';
import { useHandleClickDownloadReport } from './hooks/useHandleClickDownloadReport';

const useStyles = makeStyles(theme => ({
  body: {
    display: 'flex',
    margin: '0 10%',
    padding: '50px',
  },
  flex: {
    display: 'flex',
  },
  boldText: {
    fontWeight: 600,
  },
  leftPart: {
    display: 'flex',
    width: '50%',
    padding: '50px',
    flexDirection: 'column',
    minHeight: '150px',
    alignItems: 'center',
    borderRight: '1px solid ' + theme.palette.primary.main,
  },
  preview: {
    margin: '50px 0 50px 0',
    color: colors.primary,
    fontWeight: 600,
    fontSize: 16,
  },
  checkboxContainer: {
    display: 'flex',
    alignSelf: 'flex-start',
    marginTop: '30px',
    maxWidth: '80%',
    paddingLeft: '70px',
  },
  error: {
    color: colors.red,
  },
  rightPart: {
    display: 'flex',
    paddingLeft: '50px',
    width: '50%',
    flexDirection: 'column',
    minHeight: '150px',
    '& h2': {
      marginTop: '25px',
      fontSize: '1.2em',
    },
  },
  reportsCategoryTitle: {
    textTransform: 'uppercase',
  },
  icon: {
    margin: '50px 0 50px 0',
    fontSize: 60,
  },
  downloadIcon: {
    marginLeft: '4px',
  },
  downloadReports: {
    display: 'flex',

    '& div': {
      width: '50%',
    },
  },
  divider: {
    backgroundColor: theme.palette.primary.main,
  },
  temporarilyHidden: {
    visibility: 'hidden',
  },
  listItem: {
    height: 'auto',
    paddingTop: 0,
    paddingBottom: 0,
  },
  warning: {
    fontSize: 16,
    marginBottom: 38,
    marginTop: -15,
    color: '#566271',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    fontStyle: 'normal',
    fontWeight: 600,
    '& svg': {
      marginRight: 10,
    },
  },
}));

interface Props {
  isPreview: boolean;
  isOnError?: boolean;
  onConfirm?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  warningComponent?: React.ReactNode;
}

const ClosureSuccess: React.FC<Props> = ({ isPreview, isOnError, warningComponent, onConfirm }) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const classes = useStyles();
  const context = useContext(AppContext);
  const sortingCenterId = context.getSortingCenter?.()?.['@id'] ?? null;
  const [closure, loading] = useClosureById(id);

  const closureDate = closure ? new Date(Date.UTC(closure.year, closure.month, 0)) : null;
  const activeSortingCenterContracts = useActiveUnbalancedContractsToDate(closureDate, !loading, true);
  const sortingCenterContractsClosedThisYear = useContractsClosedThisYear(
    closureDate?.getFullYear() ?? null,
    !loading,
    true,
  );
  const clients = useClientsWithActiveContractAndContractsClosedThisYear(
    activeSortingCenterContracts,
    sortingCenterContractsClosedThisYear,
  ).sort((clientA, clientB) => {
    const nameA = clientA.shortName.toUpperCase(); // ignore upper and lowercase
    const nameB = clientB.shortName.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    // names must be equal
    return 0;
  });

  const handleClickDownloadReport = useHandleClickDownloadReport(sortingCenterId, closure, isPreview);

  if (loading) return <Loading />;

  if (!closure) {
    notify({
      message: t('closures.fetch.error'),
      severity: 'error',
    });

    return <NotFound />;
  }

  return (
    <div className={classes.body}>
      <div className={classes.leftPart}>
        {isPreview ? (
          <div className={classes.preview}>{t('closures.success.preview')}</div>
        ) : (
          <SvgIcon component={ValidateIcon} color="primary" className={classes.icon} />
        )}
        {isPreview && closure?.data?.annualClosure && typeof closure.data.finalAnnualWastagePercent === 'number' && (
          <Typography className={classes.warning}>
            <MemoInfoIcon height={24.75} width={24.75} />
            {t('closures.success.wastage', {
              finalAnnualWastagePercent: calculatePercentageWithPrecision(
                closure.data.finalAnnualWastagePercent,
              ).replace('.', ','),
            })}
          </Typography>
        )}
        <Button variant="outlined" onClick={() => handleClickDownloadReport(ReportType.ALL)}>
          {t('closures.success.download.all')}
          <DownloadIcon className={classes.downloadIcon} />
        </Button>
        {isPreview && (
          <>
            {warningComponent}
            <div className={classNames(classes.checkboxContainer, isOnError && classes.error)}>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={onConfirm}
                    color={'default'}
                    style={{ color: !!isOnError ? colors.red : colors.grey900 }}
                  />
                }
                label={t('closures.success.acknowledgementConfirm', {
                  reportNumber:
                    (clients?.length ?? 0) + (closure.data?.annualClosure ? (isPreview ? 5 : 4) : isPreview ? 4 : 3),
                })}
              />
            </div>
          </>
        )}
      </div>
      <div className={classes.rightPart}>
        <div>
          <h2 className={classes.reportsCategoryTitle}>
            {isPreview ? t('closures.success.reports.previewGlobalTitle') : t('closures.success.reports.globalTitle')}
          </h2>
        </div>
        <ListItem
          className={classes.listItem}
          button
          onClick={() => handleClickDownloadReport(ReportType.GLOBAL_THEORETICAL_AND_REALISED)}
        >
          <ListItemIcon>
            <MemoPdfIcon />
          </ListItemIcon>
          <ListItemText primary={t('closures.success.reports.list.globalTheoreticalAndRealised')} />
        </ListItem>
        <ListItem
          className={classes.listItem}
          button
          onClick={() => handleClickDownloadReport(ReportType.GLOBAL_REPORT_RESULT_AND_MATERIAL)}
        >
          <ListItemIcon>
            <MemoPdfIcon />
          </ListItemIcon>
          <ListItemText primary={t('closures.success.reports.list.globalTheoreticalResultAndMaterial')} />
        </ListItem>
        <ListItem
          className={classes.listItem}
          button
          onClick={() => handleClickDownloadReport(ReportType.GLOBAL_PRODUCTION_AND_EXPEDITION)}
        >
          <ListItemIcon>
            <MemoPdfIcon />
          </ListItemIcon>
          <ListItemText primary={t('closures.success.reports.list.globalProductionsAndExpeditions')} />
        </ListItem>
        <ListItem
          className={classes.listItem}
          button
          onClick={() => handleClickDownloadReport(ReportType.CUMULATED_MONTHLY_REPORT_EXPORTER)}
        >
          <ListItemIcon>
            <MemoPdfIcon />
          </ListItemIcon>
          <ListItemText primary={t('closures.success.reports.list.cumulatedMonthlyReport')} />
        </ListItem>
        {isPreview && (
          <ListItem
            className={classes.listItem}
            button
            onClick={() => handleClickDownloadReport(ReportType.DISTRIBUTION_KEYS)}
          >
            <ListItemIcon>
              <MemoPdfIcon />
            </ListItemIcon>
            <ListItemText primary={t('closures.success.reports.list.distributionKeys')} />
          </ListItem>
        )}
        {closure.data?.annualClosure && (
          <ListItem
            className={classes.listItem}
            button
            onClick={() => handleClickDownloadReport(ReportType.STOCKS_AMONTS_FINAUX)}
          >
            <ListItemIcon>
              <MemoPdfIcon />
            </ListItemIcon>
            <ListItemText primary={t('closures.success.reports.list.stocksAmontsFinaux')} />
          </ListItem>
        )}
        <div>
          <h2 className={classes.reportsCategoryTitle}>
            {isPreview ? t('closures.success.reports.previewClientTitle') : t('closures.success.reports.clientTitle')}
          </h2>
        </div>
        {clients?.map((client: Client) => (
          <ListItem
            className={classes.listItem}
            button
            key={client['@id']}
            onClick={() => handleClickDownloadReport(ReportType.CLIENT, client?.id)}
          >
            <ListItemIcon>
              <MemoPdfIcon />
            </ListItemIcon>
            <ListItemText primary={t('closures.success.reports.list.monthlyResults') + ' [' + client.shortName + ']'} />
          </ListItem>
        ))}
      </div>
    </div>
  );
};
export default ClosureSuccess;
